import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { Loader2Icon } from "lucide-react";
import * as React from "react";
import { PiCaretDownBold } from "react-icons/pi";
import { cn } from "../cn";
const buttonBaseClassName = "inline-flex select-none items-center justify-center whitespace-nowrap rounded-full font-title font-medium transition-[color,background-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
// Edges use inset box-shadow, not border, so hover thickening causes no layout shift.
const buttonVariantClassNames = {
    outline: "bg-primary text-primary shadow-[inset_0_0_0_1px_hsl(var(--color-neutral-700))] hover:text-accent hover:shadow-[inset_0_0_0_2px_hsl(var(--color-brand-electric-blue))] active:bg-brand active:text-primary-inverse active:shadow-[inset_0_0_0_2px_hsl(var(--color-brand-dark-blue))]",
    primary: "bg-[hsl(var(--color-brand-medium-blue))] text-primary-inverse hover:bg-brand-highlight active:bg-brand",
    secondary: "bg-[hsl(var(--color-neutral-900)/0.05)] text-primary hover:bg-[hsl(var(--color-neutral-900)/0.11)] active:bg-[hsl(var(--color-neutral-900)/0.17)]",
    ghost: "text-primary hover:bg-[hsl(var(--color-neutral-900)/0.05)] active:bg-[hsl(var(--color-neutral-900)/0.11)]",
    destructive: "bg-primary text-danger shadow-[inset_0_0_0_1px_hsl(var(--color-danger-700))] hover:shadow-[inset_0_0_0_2px_hsl(var(--color-danger-700))] active:bg-danger-solid active:text-white",
    accent: "relative isolate text-primary before:absolute before:inset-0 before:rounded-full before:[background:var(--gradient-accent)] before:content-[''] before:-z-10 after:absolute after:inset-[1.5px] after:rounded-full after:bg-primary after:content-[''] after:-z-10 hover:text-accent hover:after:inset-[2px] active:before:[background:hsl(var(--color-brand-dark-blue))] active:after:hidden active:text-white",
};
const buttonSizeClassNames = {
    sm: "h-8 px-3 text-xs gap-1",
    md: "h-10 px-4 text-sm gap-1.5",
    lg: "h-11 px-5 text-base gap-2",
};
// Padding is trimmed on the icon side — Lucide glyphs carry their own air.
const iconSidePaddingClassNames = {
    sm: { left: "pl-2", right: "pr-2" },
    md: { left: "pl-3", right: "pr-3" },
    lg: { left: "pl-4", right: "pr-4" },
};
const iconOnlySizeClassNames = {
    sm: "size-8",
    md: "size-10",
    lg: "size-11",
};
// Labelled glyphs defer to the label; icon-only glyphs are larger to fill the circle.
const labelledIconGlyphSizeClassNames = {
    sm: "[&_svg]:size-3.5",
    md: "[&_svg]:size-4",
    lg: "[&_svg]:size-[18px]",
};
const iconOnlyGlyphSizeClassNames = {
    sm: "[&_svg]:size-4",
    md: "[&_svg]:size-5",
    lg: "[&_svg]:size-5",
};
// Class builder, exported for consumers that need button styling on a non-button element.
export function buttonVariants({ className, variant = "outline", size = "md", iconOnly = false, } = {}) {
    const v = variant ?? "outline";
    const s = size ?? "md";
    return cn(buttonBaseClassName, buttonVariantClassNames[v], iconOnly ? iconOnlySizeClassNames[s] : buttonSizeClassNames[s], iconOnly
        ? iconOnlyGlyphSizeClassNames[s]
        : labelledIconGlyphSizeClassNames[s], className);
}
function isIconOnly(props) {
    return "icon" in props && props.icon !== undefined;
}
const Button = React.forwardRef((props, ref) => {
    if (isIconOnly(props)) {
        const { icon, variant = "outline", size = "md", loading = false, trackingName, disabled, className, ...domProps } = props;
        return (_jsx("button", { className: buttonVariants({
                variant,
                size,
                iconOnly: true,
                className,
            }), "data-icon-only": "", "data-dd-action-name": trackingName, disabled: disabled || loading, ref: ref, type: domProps.type ?? "button", ...domProps, children: loading ? _jsx(Loader2Icon, { className: "animate-spin" }) : icon }));
    }
    const { children, leftIcon, rightIcon, asChild = false, variant = "outline", size = "md", loading = false, trackingName, disabled, className, ...domProps } = props;
    const Comp = asChild ? Slot : "button";
    const hasLeading = Boolean(leftIcon) || loading;
    const hasTrailing = Boolean(rightIcon);
    const sidePadding = iconSidePaddingClassNames[size];
    return (_jsxs(Comp, { className: buttonVariants({
            variant,
            size,
            // Before `className` so consumers can override; tailwind-merge resolves pl/pr vs px.
            className: cn(hasLeading && sidePadding.left, hasTrailing && sidePadding.right, className),
        }), "data-dd-action-name": trackingName, disabled: asChild ? undefined : disabled || loading, ref: ref, ...domProps, children: [loading && _jsx(Loader2Icon, { className: "animate-spin" }), !loading && leftIcon && (_jsx("span", { className: "inline-flex shrink-0", children: leftIcon })), _jsx(Slottable, { children: children }), rightIcon && _jsx("span", { className: "inline-flex shrink-0", children: rightIcon })] }));
});
Button.displayName = "Button";
export { Button };
// Knob fill, opaque on interaction so the primary's fill can't bleed through. ghost
// goes darker since its pill has no rest fill for a lighter knob to read against.
const knobToneClassNames = {
    secondary: "peer-hover:bg-secondary hover:bg-[hsl(var(--color-neutral-200))] data-[state=open]:bg-[hsl(var(--color-neutral-300))]",
    outline: "peer-hover:bg-[hsl(var(--color-neutral-900)/0.08)] hover:bg-[hsl(var(--color-neutral-900)/0.12)] data-[state=open]:bg-[hsl(var(--color-neutral-900)/0.16)]",
    ghost: "peer-hover:bg-[hsl(var(--color-neutral-200))] hover:bg-[hsl(var(--color-neutral-300))] data-[state=open]:bg-[hsl(var(--color-neutral-400))]",
};
// ghost-only whole-pill hover fill. `has-[:hover]` (not the knob) stops it flickering
// as the pointer crosses the knob's edge.
const splitWrapperToneClassNames = {
    secondary: "",
    outline: "",
    ghost: "rounded-full transition-[background-color] has-[:hover]:bg-[hsl(var(--color-neutral-900)/0.05)] has-[[data-state=open]]:bg-[hsl(var(--color-neutral-900)/0.05)]",
};
const splitSizeClassNames = {
    sm: {
        primaryPadding: "pr-8",
        knob: "top-1 right-1 bottom-1 w-6",
        chevron: "size-3.5",
    },
    md: {
        primaryPadding: "pr-[41px]",
        knob: "top-[5px] right-[5px] bottom-[5px] w-[30px]",
        chevron: "size-[18px]",
    },
    lg: {
        primaryPadding: "pr-[47px]",
        knob: "top-[5px] right-[5px] bottom-[5px] w-[34px]",
        chevron: "size-5",
    },
};
const splitKnobBaseClassName = "group absolute z-10 flex items-center justify-center rounded-full text-tertiary outline-none transition-[background-color,transform] focus-visible:ring-2 focus-visible:ring-info active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50";
const SplitButtonContext = React.createContext(null);
function useSplitButtonContext(component) {
    const ctx = React.useContext(SplitButtonContext);
    if (!ctx) {
        throw new Error(`<${component}> must be used inside <SplitButton>`);
    }
    return ctx;
}
/**
 * A pill split into a primary action and an inset circular knob. The knob must stay a
 * direct DOM sibling of the action, so a wrapping trigger must be `asChild`.
 */
export function SplitButton({ variant = "secondary", size = "md", className, children, ...props }) {
    return (_jsx(SplitButtonContext.Provider, { value: { variant, size }, children: _jsx("div", { className: cn("relative inline-flex w-fit", splitWrapperToneClassNames[variant], className), ...props, children: children }) }));
}
export function SplitButtonAction({ children, leftIcon, onClick, asChild, disabled, className, }) {
    const { variant, size } = useSplitButtonContext("SplitButtonAction");
    return (_jsx(Button, { variant: variant, size: size, leftIcon: leftIcon, onClick: onClick, asChild: asChild, disabled: disabled, 
        // peer lets the knob react to hover; ghost drops its own fill to avoid doubling.
        className: cn("peer justify-start", splitSizeClassNames[size].primaryPadding, variant === "ghost" && "hover:bg-transparent", className), children: children }));
}
// forwardRef + prop spread so it slots into any `asChild` trigger.
export const SplitButtonKnob = React.forwardRef(function SplitButtonKnob({ icon, className, type = "button", ...props }, ref) {
    const { variant, size } = useSplitButtonContext("SplitButtonKnob");
    const sizing = splitSizeClassNames[size];
    return (_jsx("button", { ref: ref, type: type, "data-knob": true, className: cn(splitKnobBaseClassName, sizing.knob, knobToneClassNames[variant], className), ...props, children: icon ?? (_jsx(PiCaretDownBold, { className: cn(sizing.chevron, "transition-transform duration-200 group-data-[state=open]:rotate-180") })) }));
});
